import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ArrowBack, CheckCircle, Cancel, History, SmartToy } from '@mui/icons-material';
import { format } from 'date-fns';
import { getCourseQuizAttempts, QuizAttemptResponseDTO } from '../services/quizService';
import { courseService } from '../services/courseService';

const StudentQuizHistoryPage: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const [attempts, setAttempts] = useState<QuizAttemptResponseDTO[]>([]);
  const [courseName, setCourseName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!courseId) return;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const id = parseInt(courseId, 10);
        const [course, history] = await Promise.all([
          courseService.getCourseById(id).catch(() => null),
          getCourseQuizAttempts(id),
        ]);
        setCourseName(course?.name ?? `Course ${courseId}`);
        setAttempts(history ?? []);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to load quiz history';
        setError(message);
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [courseId]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return '—';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
    } catch {
      return dateString;
    }
  };

  const formatScore = (score: QuizAttemptResponseDTO['score']) => {
    if (typeof score === 'number') return `${score.toFixed(1)}%`;
    return 'N/A';
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body2"
          onClick={() => navigate('/student-courses')}
          sx={{ cursor: 'pointer' }}
        >
          My Courses
        </Link>
        <Link
          component="button"
          variant="body2"
          onClick={() => navigate(`/student-courses/${courseId}`)}
          sx={{ cursor: 'pointer' }}
        >
          {courseName || 'Course'}
        </Link>
        <Typography variant="body2" color="text.primary">
          Quiz history
        </Typography>
      </Breadcrumbs>

      <Box display="flex" alignItems="center" gap={1} mb={3}>
        <IconButton onClick={() => navigate(`/student-courses/${courseId}`)} aria-label="Back to course">
          <ArrowBack />
        </IconButton>
        <History color="primary" />
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Practice quiz history
          </Typography>
          <Typography variant="body2" color="text.secondary">
            All practice attempts in this course, including quizzes taken via StudyStride and MCP agents.
          </Typography>
        </Box>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : attempts.length === 0 ? (
        <Alert severity="info">
          No practice quiz attempts yet. Open a topic and use the Practice Quiz card to get started.
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Topic</TableCell>
                <TableCell>Score</TableCell>
                <TableCell>Result</TableCell>
                <TableCell>Correct</TableCell>
                <TableCell>Source</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {attempts.map((attempt) => (
                <TableRow
                  key={`${attempt.agentGenerated ? 'agent' : 'quiz'}-${attempt.id}`}
                  hover
                  sx={{ cursor: attempt.topicId ? 'pointer' : 'default' }}
                  onClick={() => {
                    if (attempt.topicId && courseId) {
                      navigate(`/student-courses/${courseId}/subjects/0/topics/${attempt.topicId}`);
                    }
                  }}
                >
                  <TableCell>{formatDate(attempt.completedAt)}</TableCell>
                  <TableCell>{attempt.topicTitle ?? `Topic ${attempt.topicId}`}</TableCell>
                  <TableCell>
                    <Typography fontWeight={600}>{formatScore(attempt.score)}</Typography>
                    {attempt.passThresholdPercent != null && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        Pass: {attempt.passThresholdPercent}%
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      icon={attempt.passed ? <CheckCircle /> : <Cancel />}
                      label={attempt.passed ? 'Passed' : 'Failed'}
                      color={attempt.passed ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    {attempt.correctAnswers ?? '—'} / {attempt.totalQuestions ?? '—'}
                  </TableCell>
                  <TableCell>
                    {attempt.agentGenerated ? (
                      <Chip size="small" icon={<SmartToy />} label="Agent / MCP" variant="outlined" />
                    ) : (
                      <Chip size="small" label="StudyStride quiz" variant="outlined" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Box mt={3}>
        <Button variant="outlined" onClick={() => navigate(`/student-courses/${courseId}`)}>
          Back to course
        </Button>
      </Box>
    </Container>
  );
};

export default StudentQuizHistoryPage;
